import { type ReactNode } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/formatters';
import type { SortField } from '../types';

type Col = { key: SortField | ''; label: string; subtitle?: ReactNode };

function collectorLabel(t?: number, a?: number): string {
  if (t == null) return 'Collector';
  return a == null || a === t ? `Collectors (${t})` : `Collectors ${a} (${t})`;
}

function buildCols(
  t: number | undefined,
  a: number | undefined,
  invSub: ReactNode
): Col[] {
  return [
    { key: '', label: '#' },
    { key: '', label: '' },
    { key: '', label: collectorLabel(t, a) },
    { key: 'quantity', label: 'Creadores' },
    { key: 'avgFollowers', label: 'Avg Followers' },
    { key: 'topCategory', label: 'Categoría' },
    { key: '', label: 'Inversión', subtitle: invSub },
  ];
}
const TH =
  'px-md py-sm text-[11px] font-medium uppercase tracking-wider select-none transition-colors';

type CellProps = {
  col: Col;
  active: boolean;
  desc: boolean;
  onClick: (() => void) | undefined;
};

function HeaderCell({ col, active, desc, onClick }: CellProps): ReactNode {
  const Icon = desc ? ChevronDown : ChevronUp;
  const cls = cn(
    TH,
    onClick && 'cursor-pointer',
    active
      ? 'text-text-secondary'
      : 'text-text-tertiary/50 hover:text-text-tertiary'
  );
  return (
    <th onClick={onClick} className={cls}>
      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
        {col.label}
        {active && <Icon size={11} className="text-brand-red" />}
      </span>
      {col.subtitle && (
        <p className="mt-0.5 whitespace-nowrap text-[10px] font-normal normal-case tracking-normal text-text-tertiary tabular-nums">
          {col.subtitle}
        </p>
      )}
    </th>
  );
}

type HeaderProps = {
  sortField: SortField;
  desc: boolean;
  onSort: (f: SortField) => void;
  total: number | undefined;
  active: number | undefined;
  investment: { total: number; goal: number } | undefined;
};

function cellClick(
  col: Col,
  onSort: HeaderProps['onSort']
): (() => void) | undefined {
  return col.key !== '' ? () => onSort(col.key as SortField) : undefined;
}

function buildInvSub(inv: HeaderProps['investment']): ReactNode {
  if (!inv) return null;
  return (
    <>
      <span className="text-success">{formatCurrency(inv.total)}</span>
      {' / '}
      {formatCurrency(inv.goal)}
    </>
  );
}

export function LeaderboardHeader(p: HeaderProps): ReactNode {
  const cols = buildCols(p.total, p.active, buildInvSub(p.investment));
  return (
    <thead>
      <tr className="border-b border-white/[0.06]">
        {cols.map((col, i) => (
          <HeaderCell
            key={i}
            col={col}
            active={col.key !== '' && p.sortField === col.key}
            desc={p.desc}
            onClick={cellClick(col, p.onSort)}
          />
        ))}
      </tr>
    </thead>
  );
}
